import PropTypes from 'prop-types';
import React, { Suspense, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { connect } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { trackEvent } from '#src/api';
import { createEntryObserver } from '../../lib/public-entry-language';
import { discardHomepageGuideContext, homepageGuideStorage } from '../utils/homepageGuidePromotion';
import { getTeam } from '../actions/team.actions';
import { setUserFromSession } from '../actions/user.actions';
import { ButtonV2 } from './base/ButtonV2';
import Modal from './base/Modal';
import Spinner from './base/Spinner';
import Notifications from './notification/Notifications';
import AccountHelp from './pages/AccountHelp';
import { BLOG_CATALOG } from './pages/Blog/BlogIndex';
import lazyPage from '../utils/lazyPage';
import { dropFastJoin } from '../utils/fastJoin';
import { dropInitialApp } from '../utils/initialApp';
import { dropInitialPricing } from '../utils/initialPricing';

// Each route is its own chunk (P1-5); the lobby chunk is also preloaded from
// <head> on /lobby/* (scripts/route-preload-plugin.cjs).
const loadBlog = () => import(/* webpackChunkName: "blog" */'./pages/Blog');
const About = lazyPage(() => import(/* webpackChunkName: "about" */'./pages/About'));
const Contact = lazyPage(() => import(/* webpackChunkName: "contact" */'./pages/Contact'));
const Home = lazyPage(() => import(/* webpackChunkName: "home" */'./pages/home/Home'));
const LandingPage = lazyPage(() => import(/* webpackChunkName: "landing" */'./pages/home/LandingPage'));
const Lobby = lazyPage(() => import(/* webpackChunkName: "lobby" */'./pages/Lobby'));
// /login: the usual page, or its checkout version on the way to checkout (TEAMBRIDGE_CHECKOUT_SIGNUP_CONTEXT).
const Login = lazyPage(() => import(/* webpackChunkName: "login" */'./pages/CheckoutSignup'));
const ResetPassword = lazyPage(() => import(/* webpackChunkName: "login" */'./pages/ResetPassword'));
const Pricing = lazyPage(() => import(/* webpackChunkName: "pricing" */'./pages/Pricing'));
const PlanCostCalculator = lazyPage(() => import(/* webpackChunkName: "pricing-calculator" */'./pages/PlanCostCalculator'));
const PrivacyPolicy = lazyPage(() => import(/* webpackChunkName: "legal" */'./pages/PrivacyPolicy'));
const Profile = lazyPage(() => import(/* webpackChunkName: "profile" */'./pages/Profile'));
const Team = lazyPage(() => import(/* webpackChunkName: "team" */'./pages/Team'));
const TermsOfService = lazyPage(() => import(/* webpackChunkName: "legal" */'./pages/TermsOfService'));
const PlanPage = lazyPage(() => import(/* webpackChunkName: "plan" */'./pages/Plan/PlanPage'));
const GamePage = lazyPage(() => import(/* webpackChunkName: "game-page" */'./pages/Game/GamePage'));
const PlayPlanPage = lazyPage(() => import(/* webpackChunkName: "game-page" */'./pages/Play/PlayPlanPage'));
const DesignSystem = lazyPage(() => import(/* webpackChunkName: "design" */'../design/DesignSystemPage'));
const Page404 = lazyPage(() => import(/* webpackChunkName: "about" */'./pages/Page404'));
const Blog = lazyPage(loadBlog);
const EmployeeAppreciation = lazyPage(() => import(/* webpackChunkName: "blog" */'./pages/Blog/EmployeeAppreciation'));
const BlogPost = lazyPage(() => loadBlog().then(({
  BLOGS
}) => ({
  default: ({
    path
  }) => BLOGS.find(blog => blog.path === path).element
})));
const ROUTE_PAGES = [[/^\/app$/, Home], [/^\/(landing)?$/, LandingPage], [/^\/lobby\//, Lobby], [/^\/profile$/, Profile], [/^\/contact$/, Contact], [/^\/termsofservice$/, TermsOfService], [/^\/privacy$/, PrivacyPolicy], [/^\/pricing$/, Pricing], [/^\/team-game-cost-calculator$/, PlanCostCalculator], [/^\/login$/, Login], [/^\/reset-password\//, ResetPassword], [/^\/team(\/|$)/, Team], [/^\/about$/, About], [/^\/game\//, GamePage], [/^\/employee-appreciation-week-games$/, EmployeeAppreciation], [/^\/blog(\/|$)/, Blog], [/^\/plan\/?$/, PlanPage], [/^\/play$/, PlayPlanPage]];

// Start the current page's chunk now, in parallel with the session request.
export function preloadPage(pathname) {
  const match = ROUTE_PAGES.find(([pattern]) => pattern.test(pathname));
  if (match) match[1].preload().catch(() => {});
}
const observePublicEntry = createEntryObserver();
if (typeof window !== 'undefined') preloadPage(window.location.pathname);
const styles = createUseStyles({
  spinnerPosition: {
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
});
const App = ({
  dispatch,
  user,
  notifications
}) => {
  const classes = styles();
  const [httpModalOpen, setHttpModalOpen] = React.useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  useEffect(() => {
    discardHomepageGuideContext(homepageGuideStorage(window), location.pathname);
  }, [location.pathname]);
  useEffect(() => {
    dispatch(setUserFromSession(window.location.search, window.location.pathname));
    window.showHidden = searchParams.get('showHidden') || window.location.host.includes('localhost');
  }, []);

  // A lobby's HTML name step (utils/fastJoin.js) can't be used without a session: show the error instead.
  useEffect(() => {
    if (user?.sessionStatus === 'error') dropFastJoin();
  }, [user?.sessionStatus]);
  // /app's HTML first screen (utils/initialApp.js) belongs to /app: an error or another page shows instead.
  useEffect(() => {
    if (user?.sessionStatus === 'error' || !/^\/app\/?$/.test(location.pathname)) dropInitialApp();
  }, [user?.sessionStatus, location.pathname]);
  // /pricing's HTML text (utils/initialPricing.js) belongs to /pricing: an error or another page shows instead.
  useEffect(() => {
    if (user?.sessionStatus === 'error' || location.pathname !== '/pricing') dropInitialPricing();
  }, [user?.sessionStatus, location.pathname]);
  useEffect(() => {
    if (user.loaded) {
      dispatch(getTeam());
    }
    if (user.loaded && user.user === null && window.location.protocol === 'https:') {
      setHttpModalOpen(true);
    }
  }, [user]);
  useEffect(() => {
    const queryParams = {};
    Array.from(new URL(document.location).searchParams).forEach(([key, value]) => {
      queryParams[key] = value;
    });
    trackEvent('Loaded a Page', {
      path: location.pathname,
      referrer: document.referrer,
      ...queryParams
    });
  }, [location]);
  useEffect(() => {
    let operatorTest = false;
    try {
      operatorTest = window.localStorage.getItem('teambridge_operator_test') === 'true';
    } catch (_) {/* Optional marker. */}
    observePublicEntry({
      loaded: user?.loaded,
      user: user?.user,
      path: location.pathname,
      hostname: window.location.hostname,
      language: window.navigator.language,
      operatorTest
    }, trackEvent);
  }, [user?.loaded, user?.user?._id, user?.user?.username, location.pathname]);
  if (location.pathname === '/account-help' || location.pathname === '/account-help/') {
    return /*#__PURE__*/React.createElement(AccountHelp, {
      user: user?.sessionStatus === 'error' ? null : user?.user
    });
  }

  // The planner (an ads landing page) needs no session to show a lineup: render it at once.
  if (location.pathname === '/plan' || location.pathname === '/plan/') {
    return /*#__PURE__*/React.createElement(Suspense, {
      fallback: null
    }, /*#__PURE__*/React.createElement(PlanPage, null));
  }
  if (user?.sessionStatus === 'error') {
    return /*#__PURE__*/React.createElement("section", {
      role: "alert",
      style: {
        maxWidth: 480,
        margin: '3rem auto',
        padding: 24
      }
    }, /*#__PURE__*/React.createElement("h1", null, "We couldn\u2019t load your session"), /*#__PURE__*/React.createElement("p", null, "Check your connection, then try again."), /*#__PURE__*/React.createElement(ButtonV2, {
      onClick: () => dispatch(setUserFromSession(window.location.search, window.location.pathname))
    }, "Try again"));
  }
  if (!user?.loaded) {
    return /*#__PURE__*/React.createElement(Spinner, {
      isBig: true,
      className: classes.spinnerPosition
    });
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ToastContainer, {
    hideProgressBar: true,
    autoClose: 25000
  }), /*#__PURE__*/React.createElement(Notifications, {
    notifications: notifications
  }), /*#__PURE__*/React.createElement(Modal, {
    style: {
      display: 'flex',
      flexDirection: 'column',
      gap: '.5rem'
    },
    open: httpModalOpen
  }, /*#__PURE__*/React.createElement("h2", null, "This site requires https"), /*#__PURE__*/React.createElement("p", null, "Please access this site directly by clicking the button below"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'flex-end'
    }
  }, /*#__PURE__*/React.createElement(ButtonV2, {
    target: "_blank",
    href: "https://www.teambridge.app"
  }, "View"))), /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(Spinner, {
      isBig: true,
      className: classes.spinnerPosition
    })
  }, /*#__PURE__*/React.createElement(Routes, null, user?.user?.username ? /*#__PURE__*/React.createElement(Route, {
    path: "/",
    element: /*#__PURE__*/React.createElement(Home, null),
    exact: true
  }) : /*#__PURE__*/React.createElement(Route, {
    path: "/",
    element: /*#__PURE__*/React.createElement(LandingPage, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/",
    element: /*#__PURE__*/React.createElement(Home, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/app",
    element: /*#__PURE__*/React.createElement(Home, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/landing",
    element: /*#__PURE__*/React.createElement(LandingPage, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/lobby/:hash",
    element: /*#__PURE__*/React.createElement(Lobby, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/profile",
    element: /*#__PURE__*/React.createElement(Profile, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/contact",
    element: /*#__PURE__*/React.createElement(Contact, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/termsofservice",
    element: /*#__PURE__*/React.createElement(TermsOfService, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/privacy",
    element: /*#__PURE__*/React.createElement(PrivacyPolicy, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/pricing",
    element: /*#__PURE__*/React.createElement(Pricing, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/team-game-cost-calculator",
    element: /*#__PURE__*/React.createElement(PlanCostCalculator, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/login",
    element: /*#__PURE__*/React.createElement(Login, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/reset-password/:token",
    element: /*#__PURE__*/React.createElement(ResetPassword, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/team/:hash?",
    element: /*#__PURE__*/React.createElement(Team, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/about",
    element: /*#__PURE__*/React.createElement(About, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/404",
    element: /*#__PURE__*/React.createElement(Page404, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/design",
    element: /*#__PURE__*/React.createElement(DesignSystem, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/game/:game",
    element: /*#__PURE__*/React.createElement(GamePage, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/play",
    element: /*#__PURE__*/React.createElement(PlayPlanPage, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/team-event-planner",
    element: /*#__PURE__*/React.createElement(Navigate, {
      to: "/plan",
      replace: true
    }),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/employee-appreciation-week-games",
    element: /*#__PURE__*/React.createElement(EmployeeAppreciation, null),
    exact: true
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/blog",
    element: /*#__PURE__*/React.createElement(Blog, null),
    exact: true
  }), BLOG_CATALOG.map(blog => /*#__PURE__*/React.createElement(Route, {
    key: blog.path,
    path: `/blog${blog.path}`,
    element: /*#__PURE__*/React.createElement(BlogPost, {
      path: blog.path
    }),
    exact: true
  })), /*#__PURE__*/React.createElement(Route, {
    path: "*",
    element: /*#__PURE__*/React.createElement(Navigate, {
      to: "/"
    })
  }))));
};
App.propTypes = {
  dispatch: PropTypes.func.isRequired,
  user: PropTypes.object,
  notifications: PropTypes.array
};
function mapStateToProps(state) {
  return {
    user: state.user,
    notifications: state.user.notifications || []
  };
}
export default connect(mapStateToProps)(App);